<!-- LauncherConfigSection.vue -->
<template>
  <div
    v-if="configType === 'launcherconfig' && !isGeneralFileUpload"
    class="space-y-6 dark:text-bg-gray"
  >
    <h2 class="font-semibold text-gray-800 dark:text-gray-200 capitalize">
      Launcher Config
    </h2>

    <div v-for="(fields, section) in displayFieldLabels" :key="section" class="glass-card p-6">
      <div class="flex items-center gap-2 mb-4">
        <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-blue-600 dark:text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle v-for="(c, i) in sectionMeta(section).circles" :key="'c' + i" :cx="c.cx" :cy="c.cy" :r="c.r"/>
          <path v-for="(d, i) in sectionMeta(section).paths" :key="i" :d="d"/>
        </svg>
        <h3 class="text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
          {{ sectionMeta(section).title }}
        </h3>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div v-for="(label, field) in fields" :key="field" :class="fieldMeta(field).span ? 'md:col-span-2' : ''">
          <label :for="inputId(field)" class="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-white">
            {{ label }}
            <span v-if="fieldMeta(field).optional" class="text-gray-400 dark:text-gray-500 font-normal">(Optional)</span>
            <span v-else class="text-red-500">*</span>
            <span v-if="definitions[field]" class="relative group inline-flex text-gray-400 dark:text-gray-500">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 16v-4"/>
                <path d="M12 8h.01"/>
              </svg>
              <span class="absolute bottom-full left-0 bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none whitespace-nowrap z-10">
                {{ definitions[field] }}
              </span>
            </span>
          </label>
          <div class="relative mt-1">
            <span class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 dark:text-gray-500">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle v-for="(c, i) in fieldIcon(section, field).circles" :key="'c' + i" :cx="c.cx" :cy="c.cy" :r="c.r"/>
                <path v-for="(d, i) in fieldIcon(section, field).paths" :key="i" :d="d"/>
              </svg>
            </span>
            <input
              :id="inputId(field)"
              v-model="modelValue[field]"
              :type="fieldMeta(field).type || 'text'"
              :min="fieldMeta(field).min"
              :placeholder="fieldMeta(field).placeholder || 'Enter ' + label"
              @focus="$emit('clear-error', field)"
              :class="[
                'w-full border rounded-lg pl-10 pr-3 py-2 focus:outline-none focus:ring-2 dark:bg-gray-600 dark:text-gray-50',
                errors[field]
                  ? 'border-red-500 ring-2 ring-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-500'
              ]"
            />
          </div>
          <p v-if="fieldMeta(field).hint" class="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {{ fieldMeta(field).hint }}
          </p>
          <FieldError :message="errors[field]" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import FieldError from '../FieldError.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  },
  fieldLabels: {
    type: Object,
    required: true
  },
  definitions: {
    type: Object,
    required: true
  },
  configType: {
    type: String,
    required: true
  },
  spaceType: {
    type: String,
    default: 'serverspace'
  },
  isGeneralFileUpload: {
    type: Boolean,
    default: false
  },
  errors: {
    type: Object,
    default: () => ({})
  }
})

// Alloy paths are serverspace-only (rendered into launcher.conf). Hide them for
// workspace launcher configs so the form isn't confusing.
const SERVERSPACE_ONLY_FIELDS = ['alloy', 'alloy_config']

const displayFieldLabels = computed(() => {
  if (props.spaceType !== 'workspace') return props.fieldLabels

  const filtered = {}
  for (const [section, fields] of Object.entries(props.fieldLabels)) {
    filtered[section] = Object.fromEntries(
      Object.entries(fields).filter(([field]) => !SERVERSPACE_ONLY_FIELDS.includes(field))
    )
  }
  return filtered
})

const ICONS = {
  link: {
    paths: [
      'M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71',
      'M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71'
    ]
  },
  folder: {
    paths: ['M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z']
  },
  sliders: {
    paths: ['M20 7h-9', 'M14 17H5'],
    circles: [{ cx: 17, cy: 17, r: 3 }, { cx: 7, cy: 7, r: 3 }]
  },
  clock: {
    paths: ['M12 6v6l4 2'],
    circles: [{ cx: 12, cy: 12, r: 10 }]
  },
  layers: {
    paths: [
      'm12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z',
      'm22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65',
      'm22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65'
    ]
  },
  globe: {
    paths: ['M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20', 'M2 12h20'],
    circles: [{ cx: 12, cy: 12, r: 10 }]
  }
}

const SECTION_META = {
  urls: { title: 'URLs', ...ICONS.link },
  paths: { title: 'Paths', ...ICONS.folder },
  settings: { title: 'Settings', ...ICONS.sliders }
}

// Per-field overrides: input type, optional flag, placeholder, hint, icon, layout.
const FIELD_META = {
  register_url: { type: 'url' },
  checkin_url: { type: 'url' },
  update_url: { type: 'url' },
  mimir_url: { type: 'url' },
  checkin_interval: { type: 'number', min: 1, icon: ICONS.clock },
  group_id: { optional: true, icon: ICONS.layers },
  proxy_url: {
    optional: true,
    type: 'url',
    // #proxy_url is claimed by the alloy section's tutorial anchors
    id: 'launcher_proxy_url',
    span: true,
    icon: ICONS.globe,
    placeholder: 'http://proxy.example.com:3128',
    hint: 'Leave empty for a direct connection. The launcher routes all requests to the licensing server through this proxy.'
  }
}

const SECTION_FIELD_ICONS = {
  urls: ICONS.link,
  paths: ICONS.folder,
  settings: ICONS.sliders
}

function sectionMeta(section) {
  return SECTION_META[section] || { title: section, paths: [], circles: [] }
}

function fieldMeta(field) {
  return FIELD_META[field] || {}
}

function fieldIcon(section, field) {
  return fieldMeta(field).icon || SECTION_FIELD_ICONS[section] || ICONS.sliders
}

function inputId(field) {
  return fieldMeta(field).id || field
}

defineEmits(['update:modelValue', 'clear-error'])
</script>
